import * as types from '../mutation-types';
import ContactAPI from '../../api/contacts';
import ConversationApi from '../../api/conversations';
import camelcaseKeys from 'camelcase-keys';

export const createMessagePayload = (payload, message) => {
  const { content, cc_emails, bcc_emails } = message;
  payload.append('message[content]', content);
  if (cc_emails) payload.append('message[cc_emails]', cc_emails);
  if (bcc_emails) payload.append('message[bcc_emails]', bcc_emails);
};

export const createConversationPayload = ({ params, contactId, files }) => {
  const { inboxId, message, sourceId, mailSubject, assigneeId } = params;
  const payload = new FormData();

  if (message) {
    createMessagePayload(payload, message);
  }

  if (files && files.length > 0) {
    files.forEach(file => payload.append('message[attachments][]', file));
  }

  payload.append('inbox_id', inboxId);
  payload.append('contact_id', contactId);
  payload.append('source_id', sourceId);
  payload.append('additional_attributes[mail_subject]', mailSubject);
  payload.append('assignee_id', assigneeId);

  return payload;
};

export const createWhatsAppConversationPayload = ({ params }) => {
  const { inboxId, message, contactId, sourceId, assigneeId } = params;

  const payload = {
    inbox_id: inboxId,
    contact_id: contactId,
    source_id: sourceId,
    message,
    assignee_id: assigneeId,
  };

  return payload;
};

const setNewConversationPayload = ({
  isFromWhatsApp,
  params,
  contactId,
  files,
}) => {
  if (isFromWhatsApp) {
    return createWhatsAppConversationPayload({ params });
  }
  return createConversationPayload({
    params,
    contactId,
    files,
  });
};

// Espelha o `per` do contacts/conversations_controller: uma pagina cheia e o
// unico sinal de que ainda existe historico para buscar.
export const CONTACT_CONVERSATIONS_PAGE_SIZE = 20;

// Mutation local ao modulo: como ele e namespaced, a chave nao colide com as
// globais e nao precisa entrar no mutation-types compartilhado.
export const APPEND_CONTACT_CONVERSATIONS = 'APPEND_CONTACT_CONVERSATIONS';

const state = {
  records: {},
  currentPage: {},
  hasMoreRecords: {},
  uiFlags: {
    isFetching: false,
    isFetchingMore: false,
  },
};

export const getters = {
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getContactConversation: $state => id => {
    return $state.records[Number(id)] || [];
  },
  getAllConversationsByContactId: $state => id => {
    const records = $state.records[Number(id)] || [];
    return camelcaseKeys(records, { deep: true });
  },
  getHasMoreConversations: $state => id => {
    return Boolean($state.hasMoreRecords?.[Number(id)]);
  },
  getCurrentPage: $state => id => {
    return $state.currentPage?.[Number(id)] || 1;
  },
};

export const actions = {
  create: async ({ commit }, { params, isFromWhatsApp }) => {
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isCreating: true,
    });
    const { contactId, files } = params;
    try {
      const payload = setNewConversationPayload({
        isFromWhatsApp,
        params,
        contactId,
        files,
      });

      const { data } = await ConversationApi.create(payload);
      commit(types.default.ADD_CONTACT_CONVERSATION, {
        id: contactId,
        data,
      });

      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isCreating: false,
      });
    }
  },
  // Aceita `get(contactId)` (chamadas antigas) e `get({ contactId, page })`.
  get: async ({ commit }, params) => {
    const { contactId, page = 1 } =
      typeof params === 'object' && params !== null
        ? params
        : { contactId: params };
    const isFirstPage = page === 1;
    const fetchingFlag = isFirstPage ? 'isFetching' : 'isFetchingMore';

    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      [fetchingFlag]: true,
    });
    try {
      const response = await ContactAPI.getConversations(contactId, page);
      const data = response.data.payload;
      if (isFirstPage) {
        commit(types.default.SET_CONTACT_CONVERSATIONS, {
          id: contactId,
          data,
        });
      } else {
        commit(APPEND_CONTACT_CONVERSATIONS, { id: contactId, data, page });
      }
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        [fetchingFlag]: false,
      });
    } catch (error) {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        [fetchingFlag]: false,
      });
    }
  },
  loadMore: async ({ dispatch, state: $state }, contactId) => {
    const nextPage = ($state.currentPage?.[Number(contactId)] || 1) + 1;
    await dispatch('get', { contactId, page: nextPage });
  },
};

export const mutations = {
  [types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_CONTACT_CONVERSATIONS]: ($state, { id, data }) => {
    $state.records = {
      ...$state.records,
      [id]: data,
    };
    $state.currentPage = { ...$state.currentPage, [id]: 1 };
    $state.hasMoreRecords = {
      ...$state.hasMoreRecords,
      [id]: data.length >= CONTACT_CONVERSATIONS_PAGE_SIZE,
    };
  },
  [APPEND_CONTACT_CONVERSATIONS]: ($state, { id, data, page }) => {
    const existing = $state.records[id] || [];
    // Dedup por id: a pagina seguinte pode repetir uma conversa se a ordenacao
    // mudar entre as requisicoes (ex.: conversa reaberta no meio do caminho).
    const knownIds = new Set(existing.map(conversation => conversation.id));
    const newRecords = data.filter(
      conversation => !knownIds.has(conversation.id)
    );

    $state.records = {
      ...$state.records,
      [id]: [...existing, ...newRecords],
    };
    $state.currentPage = { ...$state.currentPage, [id]: page };
    $state.hasMoreRecords = {
      ...$state.hasMoreRecords,
      [id]: data.length >= CONTACT_CONVERSATIONS_PAGE_SIZE,
    };
  },
  [types.default.ADD_CONTACT_CONVERSATION]: ($state, { id, data }) => {
    const conversations = $state.records[id] || [];

    const updatedConversations = [...conversations];
    const index = conversations.findIndex(
      conversation => conversation.id === data.id
    );

    if (index !== -1) {
      updatedConversations[index] = { ...conversations[index], ...data };
    } else {
      updatedConversations.push(data);
    }

    $state.records = {
      ...$state.records,
      [id]: updatedConversations,
    };
  },
  [types.default.DELETE_CONTACT_CONVERSATION]: ($state, id) => {
    const { [id]: deletedRecord, ...remainingRecords } = $state.records;
    $state.records = remainingRecords;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
